import React, { useState, useEffect, cloneElement, ReactElement, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import {
  Settings,
  LayoutList,
  Users,
  Trophy,
  MapPin,
  Shield,
  ShieldCheck,
  X,
  Star,
  CalendarDays,
  BarChart3,
  ArrowLeft,
  Lock,
  CheckCircle2
} from 'lucide-react';
import { Player, Team, Match, Tournament, News, Club, Config, UserProfile, RankingConfig } from '../../types';
import { auth } from '../../lib/firebase';
import { AdminJugadores } from './AdminJugadores';
import { AdminTorneos } from './AdminTorneos';
import { AdminResultados, AdminCategoryValidationSection } from './AdminResultados';
import { AdminRankingCircuito } from './AdminRankingCircuito';
import { AdminClubes } from './AdminClubes';
// AdminNovedades removed — feature deprecated
import { AdminConfig } from './AdminConfig';
import { AdminConfiguracion } from './AdminConfiguracion';
import { SuperAdminPanel } from './SuperAdminPanel';
import { AdminCategoryRequests } from './AdminCategoryRequests';
import { AdminPlayerValidations } from './AdminPlayerValidations';
import { AdminBookings } from './AdminBookings';
import { AdminAnalytics } from './AdminAnalytics';
import { SuperAdminAnalytics } from './SuperAdminAnalytics';
import { usePosSession } from '../../context/PosSessionContext';
import { PosLockScreen } from './PosLockScreen';
import { useAutoCleanup } from '../../hooks/useAutoCleanup';


interface AdminDashboardProps {
  userRole: 'user' | 'admin' | 'superadmin' | 'co_admin' | 'empleado' | 'gestor_turnos' | 'gestor_torneos';
  userId: string;
  players: Player[];
  teams: Team[];
  matches: Match[];
  tournaments: Tournament[];
  news: News[];
  clubs: Club[];
  sedesPublicas: UserProfile[];
  allUsers?: UserProfile[];
  config: Config;
  rankingConfig: RankingConfig;
  db: any;
  getCollectionPath: (col: string) => string;
  allTeams: Team[];
  handleShareCanvas: (canvas: HTMLCanvasElement, filename: string, action: 'download' | 'whatsapp') => void;
  onPlayerClick: (id: string | null) => void;
  onMatchClick?: (m: Match) => void;
  adminSection: string;
  setAdminSection: (section: string) => void;
  onOpenSupportChat: (player: Player) => void;
  onOpenChatWithPlayer?: (playerId: string, playerName: string, playerPhoto: string) => void;
  highlightBookingRequestId?: string | null;
  onHighlightBookingRequestHandled?: () => void;
}

export function AdminDashboard({
  userRole,
  userId,
  players,
  teams,
  matches,
  tournaments,
  news,
  clubs,
  sedesPublicas,
  allUsers = [],
  config,
  rankingConfig,
  db,
  getCollectionPath,
  allTeams,
  handleShareCanvas,
  onPlayerClick,
  onMatchClick,
  adminSection,
  setAdminSection,
  onOpenSupportChat,
  onOpenChatWithPlayer,
  highlightBookingRequestId,
  onHighlightBookingRequestHandled
}: AdminDashboardProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [targetMatchId, setTargetMatchId] = useState<string | null>(null);
  const [adminClubId, setAdminClubId] = useState<string | null>(null);
  const [totalPendingActions, setTotalPendingActions] = useState(0);
  const [tournamentSubSection, setTournamentSubSection] = useState<'mis_torneos' | 'partidos_resultados' | 'disputas' | 'validaciones_categorias' | 'ranking'>('mis_torneos');
  const [localSuccessMsg, setLocalSuccessMsg] = useState('');

  const { activeStaffId, setActiveStaffId, lockScreen } = usePosSession();
  const [posStaffList, setPosStaffList] = useState<any[]>([]);
  const hasCheckedFreshLoginRef = useRef(false);

  // Mantenimiento de fondo automatizado
  useAutoCleanup(userRole);

  useEffect(() => {
    if (!userId || userRole === 'superadmin') return;
    const fetchAdminClub = async () => {
      try {
        const uDoc = await getDoc(doc(db, 'users', userId));
        if (uDoc.exists()) {
          setAdminClubId(uDoc.data()?.clubId || null);
        }
      } catch (e) { console.error(e); }
    };
    fetchAdminClub();
  }, [userId, userRole, db]);

  useEffect(() => {
    if (!userId || userRole === 'superadmin') return;

    let currentAdminData: any = null;

    // Listen to admin's user doc
    const unsubUser = onSnapshot(doc(db, 'users', userId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        currentAdminData = {
          email: 'admin',
          name: data.name || 'Admin',
          role: 'admin',
          pin: data.posPin || null,
          photoUrl: data.photoUrl || null
        };
        updatePosList();
      }
    });

    let currentStaffList: any[] = [];
    const q = query(collection(db, 'staff_roles'), where('clubId', '==', userId));
    const unsubStaff = onSnapshot(q, (snap) => {
      currentStaffList = snap.docs.map(d => ({ email: d.id, ...d.data() }));
      updatePosList();
    });

    const updatePosList = () => {
      let list = [...currentStaffList].map(staff => {
        const uMatch = allUsers.find(u => u.email?.toLowerCase() === staff.email.toLowerCase());
        return {
          ...staff,
          name: uMatch?.name || staff.email.split('@')[0],
          photoUrl: uMatch?.photoUrl || null
        };
      });

      if (currentAdminData && currentAdminData.pin) {
        list.unshift(currentAdminData);
      }
      
      setPosStaffList(list);

      const currentUser = auth.currentUser;
      const isFreshLogin = currentUser?.metadata?.lastSignInTime ? 
        (new Date().getTime() - new Date(currentUser.metadata.lastSignInTime).getTime() < 5 * 60 * 1000) : false;
      const storedStaff = sessionStorage.getItem('posActiveStaffId');

      // Auto-bypass si no es admin root, o si es fresh login
      if (userRole !== 'admin') {
        setActiveStaffId(null);
      } else if (!storedStaff && !hasCheckedFreshLoginRef.current && (isFreshLogin || list.filter((s: any) => s.pin).length === 0)) {
        setActiveStaffId('admin');
      } else if (!activeStaffId) {
        // Force lock screen if someone has pin but activeStaffId is null
      }

      hasCheckedFreshLoginRef.current = true;
    };

    return () => {
      unsubUser();
      unsubStaff();
    };
  }, [db, userId, userRole, allUsers]);

  useEffect(() => {
    // Listen to tournament matches in dispute
    const matchesRef = collection(db, getCollectionPath('matches'));
    const qTournament = query(matchesRef, where('status', '==', 'disputed'));

    const unsubTournament = onSnapshot(qTournament, (snapshot) => {
      let list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (userRole !== 'superadmin') {
        list = list.filter((m: any) => m.organizerId === userId);
      }
      setTotalPendingActions(list.length);
    }, e => console.error(e));

    return () => {
      unsubTournament();
    };
  }, [db, getCollectionPath, userId, userRole, adminClubId]);

  const activeStaffData = activeStaffId ? posStaffList.find(s => s.email === activeStaffId) : null;
  const effectiveRole = activeStaffData ? activeStaffData.role : userRole;

  const isClubAdmin = ['admin', 'co_admin', 'empleado', 'gestor_turnos', 'gestor_torneos'].includes(effectiveRole);

  // Filter lists based on role and organization
  const filteredTeams = teams;
  const filteredMatches = userRole === 'superadmin' ? matches : matches.filter(m => m.organizerId === userId);
  const filteredTournaments = userRole === 'superadmin' ? tournaments : tournaments.filter(t => t.organizerId === userId);

  const currentSedeProfile = allUsers?.find(u => u.uid === userId) || sedesPublicas.find(u => u.uid === userId);
  const currentPlan = currentSedeProfile?.subscriptionPlan || 'Basico';

  const menuItems = ([
    // ── Bloque 1: Competición ──
    { id: 'torneos', label: 'Torneos', icon: <Trophy size={20} />, description: 'Mis Torneos y Resultados', group: 'competicion', allowedRoles: ['admin', 'co_admin', 'empleado', 'gestor_torneos', 'superadmin'] },
    { id: 'jugadores', label: 'Jugadores', icon: <Users size={20} />, description: 'Ranking y perfiles', group: 'competicion', superOnly: true },
    // ── Bloque 2: Ecosistema ──
    { id: 'superpanel', label: 'Gestión de Sedes', icon: <MapPin size={20} />, description: 'Clubes autorizados', group: 'ecosistema', superOnly: true },
    { id: 'clubes', label: 'Sponsors', icon: <Star size={20} />, description: 'Marcas y Patrocinios', group: 'ecosistema', feature: 'feature_clubs' as keyof Config, superOnly: true },
    // ── Bloque 3: Control Maestro ──
    { id: 'analiticas', label: 'Métricas', icon: <BarChart3 size={20} />, description: 'Estadísticas e Ingresos', group: 'control', allowedRoles: ['admin', 'co_admin', 'gestor_torneos', 'superadmin'] },
    { id: 'config', label: 'Ajustes Globales', icon: <Settings size={20} />, description: 'Identidad visual', group: 'control', superOnly: true },
  ] as { id: string; label: string; icon: any; description: string; group?: string; superOnly?: boolean; allowedRoles?: string[]; feature?: keyof Config }[]).filter(item => {
    if (item.superOnly && userRole !== 'superadmin') return false;
    if (item.allowedRoles && !item.allowedRoles.includes(effectiveRole)) return false;
    if (item.feature && config[item.feature] === false) return false;
    return true;
  });

  // Agrupación en 3 bloques para superadmin
  const competicionItems = menuItems.filter(item => item.group === 'competicion');
  const ecosistemaItems = menuItems.filter(item => item.group === 'ecosistema');
  const controlItems = menuItems.filter(item => item.group === 'control');

  const scrollRef = useRef<HTMLDivElement>(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const handleOpenAdminT = (e: any) => {
      setAdminSection('torneos');
      setTournamentSubSection('mis_torneos');
    };
    const handleNavigateToDisputas = (e: any) => {
      setAdminSection('torneos');
      setTournamentSubSection('disputas');
      if (e.detail) {
        setTargetMatchId(e.detail);
      }
    };
    const handleNavigateToResultados = (e: any) => {
      setAdminSection('torneos');
      setTournamentSubSection('partidos_resultados');
    };
    window.addEventListener('openAdminTournament', handleOpenAdminT);
    const handleNavigateToValidaciones = (e: any) => {
      setAdminSection('validaciones_disputas');
      if (e.detail) {
        setTargetMatchId(e.detail);
      }
    };
    window.addEventListener('navigateToDisputas', handleNavigateToDisputas);
    window.addEventListener('navigateToResultados', handleNavigateToResultados);
    window.addEventListener('navigateToValidaciones', handleNavigateToValidaciones);
    return () => {
      window.removeEventListener('openAdminTournament', handleOpenAdminT);
      window.removeEventListener('navigateToDisputas', handleNavigateToDisputas);
      window.removeEventListener('navigateToResultados', handleNavigateToResultados);
      window.removeEventListener('navigateToValidaciones', handleNavigateToValidaciones);
    };
  }, []);



  // Asegura que la sección de Turnos sea la activa por defecto solo la primera vez que inicia un administrador
  useEffect(() => {
    if (isClubAdmin && !hasInitializedRef.current) {
      // If gestor_torneos, turnos is not applicable, so default to torneos. Otherwise turnos.
      setAdminSection(userRole === 'gestor_torneos' ? 'torneos' : 'turnos');
      hasInitializedRef.current = true;
    }
  }, [isClubAdmin, userRole, setAdminSection]);

  // Sync scroll on section change for mobile
  useEffect(() => {
    setIsMobileMenuOpen(false);

    // Auto-center on selection
    if (scrollRef.current) {
      const activeBtn = scrollRef.current.querySelector(`[data-id="${adminSection}"]`);
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
      }
    }

    // Usar scroll inmediato (behavior: 'auto') para evitar que la transición de páginas se sienta trabada
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [adminSection]);

  const activeItem = menuItems.find(i => i.id === adminSection) || menuItems[0];
  const activeSectionLabel = adminSection === 'configuracion' ? 'Configuración' : (menuItems.find(i => i.id === adminSection)?.label || '');

  return (
    <div className="flex flex-col min-h-[600px] animate-in fade-in duration-700 relative">
      {/* POS SESSION LOCK SCREEN */}
      {!activeStaffId && userRole === 'admin' && (posStaffList.length > 1 || posStaffList.some(s => s.pin)) && (
        <PosLockScreen staffList={posStaffList} onUnlock={setActiveStaffId} />
      )}

      {localSuccessMsg && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 theme-primary-bg text-black px-6 py-3 md:px-8 md:py-4 rounded-full font-black shadow-2xl z-[9999] flex items-center animate-in fade-in slide-in-from-top-8 text-sm md:text-base w-max max-w-[90vw] border-2 border-black/20">
          <CheckCircle2 className="mr-2 md:mr-3 shrink-0" size={24} />
          <span className="truncate">{localSuccessMsg}</span>
        </div>
      )}

      {/* POS SESSION HEADER BUTTON */}
      {activeStaffId && userRole === 'admin' && (posStaffList.length > 1 || posStaffList.some(s => s.pin)) && (
        <div className="relative md:absolute md:-top-5 md:right-4 lg:right-8 z-50 animate-in fade-in slide-in-from-top-4 duration-500 mb-3 md:mb-0 w-full md:w-auto flex justify-center md:justify-end">
          <button 
            onClick={lockScreen}
            className="flex flex-row md:flex-col items-center justify-center gap-2 md:gap-0 bg-theme-primary/10 border border-theme-primary/30 text-theme-primary px-3 py-1 md:px-4 md:py-1.5 rounded-full md:rounded-2xl hover:bg-white hover:text-black hover:border-white hover:scale-105 transition-all duration-300 shadow-sm"
          >
            <div className="flex items-center gap-1.5 text-[8px] md:text-[9px] font-black uppercase tracking-widest opacity-80 md:opacity-70 md:mb-1">
              <Lock size={9} className="md:w-2.5 md:h-2.5" />
              <span className="hidden md:inline">User</span>
            </div>
            <div className="hidden md:block w-full h-px bg-current opacity-20 mb-1 transition-colors" />
            <span className="text-[9px] md:text-xs font-black truncate max-w-[150px] md:max-w-[120px] uppercase tracking-wide">
              <span className="md:hidden font-bold opacity-80">USUARIO: </span>
              {posStaffList.find(s => s.email === activeStaffId)?.name || (activeStaffId === 'admin' ? 'Admin' : activeStaffId.split('@')[0])}
            </span>
          </button>
        </div>
      )}

      {/* ADAPTIVE NAVIGATION DOCK */}
      <div className="relative z-40 w-full py-1 md:py-2 mt-4 md:mt-2">
        <div className="max-w-[90rem] mx-auto px-4 flex items-start justify-between gap-2 md:gap-4">

          {/* UNIFIED COMPACT NAVIGATION (SAME FOR MOBILE AND DESKTOP) */}
          <div className="flex-1 flex flex-col w-full max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto mb-1 space-y-3">

            {isClubAdmin ? (
              (userRole === 'gestor_torneos') ? (
                /* DISEÑO UNIFICADO Y ULTRA-PREMIUM CON BOTONES INDIVIDUALES INDEPENDIENTES PARA EVITAR RECTÁNGULOS DE TRANSICIÓN */
                <div className={`grid ${menuItems.length === 2 ? 'grid-cols-2 max-w-md mx-auto' : 'grid-cols-3'} gap-2.5 w-full animate-in fade-in duration-200`}>
                  {menuItems.map((item) => {
                    const isActive = adminSection === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setAdminSection(item.id)}
                        className={`flex flex-col md:flex-row items-center justify-center md:justify-center gap-1 md:gap-3 text-center p-2 md:py-3 md:px-6 rounded-xl md:rounded-2xl border transition-all duration-300 min-h-[54px] w-full transform active:scale-95 outline-none focus:outline-none focus:ring-0 group relative ${isActive
                          ? 'bg-[#2a2d3d] border-theme-primary text-white shadow-[0_4px_15px_rgba(var(--glow-rgb),0.2)] md:scale-[1.02]'
                          : 'bg-[#1a1c23] border-[#2d313f] text-zinc-400 hover:bg-[#242732] hover:border-[#3b3f51] hover:text-zinc-200'
                          }`}
                      >
                        <div className={`md:mb-0 mb-1 transition-all duration-300 flex items-center justify-center ${isActive
                          ? 'theme-primary-text drop-shadow-[0_4px_10px_rgba(var(--glow-rgb),0.3)] md:scale-110'
                          : 'text-zinc-400 group-hover:text-white md:group-hover:scale-105'
                          }`}>
                          <div className="md:hidden flex items-center justify-center">
                            {cloneElement(item.icon as ReactElement, { size: 12 })}
                          </div>
                          <div className="hidden md:flex items-center justify-center">
                            {cloneElement(item.icon as ReactElement, { size: 16 })}
                          </div>
                        </div>
                        <span className="text-[8px] md:text-[11px] xl:text-[12px] font-black uppercase tracking-wider leading-tight text-center md:text-left px-1 md:px-0">
                          {item.label}
                        </span>
                        {item.id === 'torneos' && totalPendingActions > 0 && (
                          <span className="absolute top-1 right-1 md:top-2 md:right-2 bg-red-500 text-white border border-red-400 text-[8px] font-black w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center shadow-lg shadow-red-500/20 animate-pulse">
                            {totalPendingActions}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : null
            ) : (
              /* SUPERADMIN: NAVEGACIÓN EN 3 BLOQUES LÓGICOS */
              <div className="space-y-2">
                {/* Helper para renderizar un grupo de botones con etiqueta */}
                {[
                  { label: '⚔️ Competición', items: competicionItems },
                  { label: '🌐 Ecosistema', items: ecosistemaItems },
                  { label: '⚙️ Control Maestro', items: controlItems },
                ].map((group, gIdx) => (
                  group.items.length > 0 && (
                    <div key={gIdx}>
                      {gIdx > 0 && <div className="h-px w-full bg-white/[0.06] my-1.5" />}
                      <div className="flex items-center gap-2 px-1 mb-1.5">
                        <span className="text-[8px] font-black uppercase tracking-[0.2em] text-white/30 select-none">{group.label}</span>
                        <div className="h-px flex-1 bg-white/[0.04]" />
                      </div>
                      <div className="flex flex-wrap justify-center gap-1.5 w-full">
                        {group.items.map((item) => {
                          const isActive = adminSection === item.id;
                          return (
                            <button
                              key={item.id}
                              onClick={() => setAdminSection(item.id)}
                              className={`flex flex-col items-center justify-center text-center p-1.5 rounded-xl border transition-all duration-300 min-h-[56px] w-[calc(25%-5px)] sm:w-[calc(20%-5px)] md:w-[calc(16.66%-6px)] lg:w-[calc(14.28%-6px)] max-w-[120px] outline-none focus:outline-none focus:ring-0 relative ${isActive
                                ? 'theme-primary-border bg-theme-primary/15 theme-primary-text shadow-[0_0_10px_rgba(var(--glow-rgb),0.15)]'
                                : 'border-white/5 bg-black/20 text-white/80 hover:bg-black/40'
                                }`}
                            >
                              <div className={`mb-0.5 transition-all duration-300 flex items-center justify-center ${isActive ? 'theme-primary-text drop-shadow-[0_4px_10px_rgba(var(--glow-rgb),0.3)]' : 'text-white/50 group-hover:text-white'}`}>
                                {cloneElement(item.icon as ReactElement, { size: 12 })}
                              </div>
                              <span className="text-[8px] md:text-[9px] font-black uppercase tracking-tight leading-none text-center px-0.5">
                                {item.label}
                              </span>
                              {item.id === 'torneos' && totalPendingActions > 0 && (
                                <span className="absolute top-1 right-1 bg-red-500 text-white border border-red-400 text-[8px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-lg shadow-red-500/20 animate-pulse">
                                  {totalPendingActions}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>



        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 min-w-0 mt-1 md:mt-5">
        {/* BOTÓN DE "VOLVER" FUERA DEL CONTENEDOR (MÓVIL) */}
        {isClubAdmin && userRole !== 'gestor_torneos' && adminSection !== 'turnos' && (
          <div className="md:hidden w-full flex justify-center mb-3 z-50 animate-in fade-in duration-200">
            <button
              onClick={() => setAdminSection('turnos')}
              className="flex items-center gap-2 px-3 py-1.5 bg-transparent border border-white/50 hover:border-white text-white rounded-full transition-colors duration-300 hover:bg-white/10 active:scale-95 cursor-pointer text-xs font-black tracking-wide outline-none focus:outline-none focus:ring-0"
            >
              <ArrowLeft size={14} className="text-white" />
              <span>Volver</span>
            </button>
          </div>
        )}

        <div className="bg-[#0f172a]/50 backdrop-blur-2xl border border-white/10 rounded-2xl md:rounded-[var(--radius-large)] p-3 md:p-10 lg:p-14 shadow-2xl relative overflow-hidden h-full min-h-[750px] w-full">
          {/* Subtle decoration */}
          <div className="absolute -top-48 -right-48 w-96 h-96 bg-theme-primary/5 blur-[120px] rounded-full pointer-events-none" />

          {/* BOTÓN DE "VOLVER" DENTRO DEL CONTENEDOR (ESCRITORIO) */}
          {isClubAdmin && userRole !== 'gestor_torneos' && adminSection !== 'turnos' && (
            <div className="hidden md:block absolute md:top-6 md:left-6 z-50 animate-in fade-in duration-200">
              <button
                onClick={() => setAdminSection('turnos')}
                className="flex items-center gap-2.5 px-5 py-2.5 bg-transparent border border-white/50 hover:border-white text-white rounded-full transition-colors duration-300 hover:bg-white/10 active:scale-95 cursor-pointer text-base font-black tracking-wide outline-none focus:outline-none focus:ring-0"
              >
                <ArrowLeft size={20} className="text-white" />
                <span>Volver</span>
              </button>
            </div>
          )}

          <div className="relative z-10">
            {adminSection === 'jugadores' && (
              <AdminJugadores
                players={players}
                userRole={userRole}
                db={db}
                getCollectionPath={getCollectionPath}
                config={config}
              />
            )}
            {adminSection === 'torneos' && (
              currentPlan === 'Basico' && userRole !== 'superadmin' ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[500px] text-center p-6 bg-white/5 rounded-3xl border border-white/10">
                  <div className="w-20 h-20 bg-theme-primary/10 rounded-full flex items-center justify-center mb-6 border border-theme-primary/20">
                    <Lock size={32} className="theme-primary-text" />
                  </div>
                  <h2 className="text-2xl font-black uppercase tracking-tight text-white mb-2">
                    Módulo de Competición Bloqueado
                  </h2>
                  <p className="text-white/60 text-sm max-w-md mb-8">
                    La creación y gestión de Torneos, Ligas y Zonas está disponible a partir del <strong>Plan Full</strong>. Aumenta tu ocupación organizando competencias oficiales.
                  </p>
                  <button onClick={() => alert('Próximamente: Redirección a pasarela de pago o contacto con ventas')} className="theme-primary-bg text-black font-black uppercase tracking-widest text-xs px-8 py-4 rounded-xl hover:scale-105 transition-all flex items-center gap-2 shadow-lg shadow-theme-primary/20">
                    <Star size={16} /> Mejorar al Plan Full
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  {/* Sub-tabs header for Torneos */}
                  <div className="flex justify-center mb-8 w-full border-b border-white/5 pb-0 relative z-20 pt-12 md:pt-4">
                    <div className="flex gap-6 w-full sm:w-auto overflow-x-auto custom-scrollbar px-4">
                      <button
                        onClick={() => setTournamentSubSection('mis_torneos')}
                        className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                          tournamentSubSection === 'mis_torneos'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
                        }`}
                      >
                        <Trophy className={`w-3.5 h-3.5 md:w-4 md:h-4 ${tournamentSubSection === 'mis_torneos' ? 'text-theme-primary' : 'opacity-70'}`} />
                        Mis Torneos
                      </button>
                      <button
                        onClick={() => setTournamentSubSection('partidos_resultados')}
                        className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                          tournamentSubSection === 'partidos_resultados'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
                        }`}
                      >
                        <LayoutList className={`w-3.5 h-3.5 md:w-4 md:h-4 ${tournamentSubSection === 'partidos_resultados' ? 'text-theme-primary' : 'opacity-70'}`} />
                        Resultados
                      </button>
                      <button
                        onClick={() => setTournamentSubSection('disputas')}
                        className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                          tournamentSubSection === 'disputas'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
                        }`}
                      >
                        <ShieldCheck className={`w-3.5 h-3.5 md:w-4 md:h-4 ${tournamentSubSection === 'disputas' ? 'text-theme-primary' : 'opacity-70'}`} />
                        <div className="flex items-center gap-1.5">
                          Disputas
                          {totalPendingActions > 0 && (
                            <span className="bg-red-500 text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-[0_0_10px_rgba(239,68,68,0.5)] border border-red-400 animate-pulse">
                              {totalPendingActions}
                            </span>
                          )}
                        </div>
                      </button>
                      <button
                        onClick={() => setTournamentSubSection('validaciones_categorias')}
                        className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                          tournamentSubSection === 'validaciones_categorias'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
                        }`}
                      >
                        <Shield className={`w-3.5 h-3.5 md:w-4 md:h-4 ${tournamentSubSection === 'validaciones_categorias' ? 'text-theme-primary' : 'opacity-70'}`} />
                        Validación Categorías
                      </button>
                      <button
                        onClick={() => setTournamentSubSection('ranking')}
                        className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                          tournamentSubSection === 'ranking'
                            ? 'border-theme-primary text-theme-primary'
                            : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
                        }`}
                      >
                        <Trophy className={`w-3.5 h-3.5 md:w-4 md:h-4 ${tournamentSubSection === 'ranking' ? 'text-theme-primary' : 'opacity-70'}`} />
                        Ranking
                      </button>
                    </div>
                  </div>

                  {tournamentSubSection === 'mis_torneos' ? (
                    <AdminTorneos
                      tournaments={filteredTournaments}
                      allTeams={filteredTeams}
                      players={players}
                      matches={filteredMatches}
                      clubs={clubs}
                      db={db}
                      getCollectionPath={getCollectionPath}
                      config={config}
                      rankingConfig={rankingConfig}
                      handleShareCanvas={handleShareCanvas}
                      onPlayerClick={onPlayerClick}
                      organizerId={userId}
                      userRole={userRole}
                      sedesPublicas={sedesPublicas}
                    />
                  ) : tournamentSubSection === 'partidos_resultados' ? (
                    <div className="h-full relative overflow-y-auto custom-scrollbar pr-2 pb-20">
                      <AdminResultados
                        players={players}
                        db={db}
                        getCollectionPath={getCollectionPath}
                        sedesPublicas={sedesPublicas}
                        config={config}
                        onMatchClick={onMatchClick}
                        initialTargetMatchId={targetMatchId}
                        onTargetMatchHandled={() => setTargetMatchId(null)}
                        userId={userId}
                        userRole={userRole}
                        mode="tournaments_only"
                      />
                    </div>
                  ) : tournamentSubSection === 'disputas' ? (
                    <div className="h-full relative overflow-y-auto custom-scrollbar pr-2 pb-20">
                      <AdminResultados
                        players={players}
                        db={db}
                        getCollectionPath={getCollectionPath}
                        sedesPublicas={sedesPublicas}
                        config={config}
                        onMatchClick={onMatchClick}
                        initialTargetMatchId={targetMatchId}
                        onTargetMatchHandled={() => setTargetMatchId(null)}
                        userId={userId}
                        userRole={userRole}
                        mode="validations_disputes"
                      />
                    </div>
                  ) : tournamentSubSection === 'validaciones_categorias' ? (
                    <div className="h-full relative overflow-y-auto custom-scrollbar">
                      <AdminCategoryValidationSection
                        players={players}
                        db={db}
                        getCollectionPath={getCollectionPath}
                        userId={userId}
                        userRole={userRole}
                        sedesPublicas={sedesPublicas}
                        setLocalSuccessMsg={(msg) => {
                          setLocalSuccessMsg(msg);
                          setTimeout(() => setLocalSuccessMsg(''), 4000);
                        }}
                      />
                    </div>
                  ) : tournamentSubSection === 'ranking' ? (
                    <div className="h-full relative overflow-y-auto custom-scrollbar">
                      <AdminRankingCircuito
                        userId={userId}
                        players={players}
                        db={db}
                        getCollectionPath={getCollectionPath}
                        config={config}
                        logoUrl={(currentSedeProfile as any)?.organizerProfile?.logoUrl || currentSedeProfile?.photoUrl || config.logoUrl}
                      />
                    </div>
                  ) : null}
                </div>
              )
            )}

            {adminSection === 'analiticas' && userRole === 'superadmin' && (
              <SuperAdminAnalytics
                players={players}
                matches={matches}
                tournaments={tournaments}
                sedesPublicas={sedesPublicas}
                allUsers={allUsers}
              />
            )}

            {adminSection === 'analiticas' && userRole !== 'superadmin' && (
              currentPlan === 'Basico' ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[500px] text-center p-6 bg-white/5 rounded-3xl border border-white/10">
                  <div className="w-20 h-20 bg-theme-primary/10 rounded-full flex items-center justify-center mb-6 border border-theme-primary/20">
                    <Lock size={32} className="theme-primary-text" />
                  </div>
                  <h2 className="text-2xl font-black uppercase tracking-tight text-white mb-2">
                    Módulo de Métricas Bloqueado
                  </h2>
                  <p className="text-white/60 text-sm max-w-md mb-8">
                    El análisis de ingresos, ocupación de canchas y proyecciones financieras avanzadas está disponible a partir del <strong>Plan Full</strong>.
                  </p>
                  <button onClick={() => alert('Próximamente: Redirección a pasarela de pago o contacto con ventas')} className="theme-primary-bg text-black font-black uppercase tracking-widest text-xs px-8 py-4 rounded-xl hover:scale-105 transition-all flex items-center gap-2 shadow-lg shadow-theme-primary/20">
                    <Star size={16} /> Mejorar al Plan Full
                  </button>
                </div>
              ) : (
                <AdminAnalytics
                  userId={userId}
                  userRole={userRole}
                  players={players}
                  sedesPublicas={sedesPublicas}
                  db={db}
                  getCollectionPath={getCollectionPath}
                />
              )
            )}



            {adminSection === 'clubes' && config.feature_clubs !== false && (
              <AdminClubes clubs={clubs} db={db} getCollectionPath={getCollectionPath} config={config} />
            )}
            {adminSection === 'configuracion' && ['admin', 'co_admin'].includes(effectiveRole) && (
              <AdminConfiguracion
                userId={userId}
                userRole={effectiveRole}
                players={players}
                db={db}
                getCollectionPath={getCollectionPath}
                config={config}
                highlightBookingRequestId={highlightBookingRequestId}
                onHighlightBookingRequestHandled={onHighlightBookingRequestHandled}
                onOpenChatWithPlayer={onOpenChatWithPlayer}
                subscriptionPlan={currentPlan}
              />
            )}
            {adminSection === 'turnos' && ['admin', 'co_admin', 'empleado', 'gestor_turnos'].includes(effectiveRole) && (
              <AdminBookings
                userId={userId}
                userRole={userRole}
                players={players}
                db={db}
                getCollectionPath={getCollectionPath}
                config={config}
                highlightRequestId={highlightBookingRequestId}
                onHighlightHandled={onHighlightBookingRequestHandled}
                onOpenChatWithPlayer={onOpenChatWithPlayer}
                onNavigateToConfiguracion={() => setAdminSection('configuracion')}
                onNavigateToSection={(section) => setAdminSection(section)}
                sedesPublicas={sedesPublicas}
                subscriptionPlan={currentPlan}
              />
            )}
            {adminSection === 'config' && userRole === 'superadmin' && <AdminConfig config={config} db={db} getCollectionPath={getCollectionPath} userId={userId} players={players} />}

            {adminSection === 'superpanel' && userRole === 'superadmin' && (
              <SuperAdminPanel config={config} db={db} getCollectionPath={getCollectionPath} userId={userId} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}
